import { getBlockIdsRecursively, ProductsBlockKind } from '@mssgme/shared';
import { createReducer, isMatchId, updateResource } from '../helpers';
import { api } from '../actions';

const getWebsiteState = (state, action) => state.find(isMatchId(action.websiteId));
const getPagesState = (state, action) => getWebsiteState(state, action).pages;
const getPageState = (state, action) => getPagesState(state, action).find(isMatchId(action.pageId));
const getBlocksState = (state, action) => getPageState(state, action).blocks;
const getMenuItemsState = (state, action) => getWebsiteState(state, action).menu.items;

const acceptWebsite = updateResource({
    match: 'websiteId',
    prepend: true,
});

const acceptPage = updateResource({
    collection: getPagesState,
    match: 'pageId',
    onUpdate: ({ state, action, created, deleted }) => {
        const items = getMenuItemsState(state, action);

        if (created) {
            items.push({
                enabled: true,
                page: created._id,
                label: '',
            });
        } else if (deleted) {
            const idx = items.findIndex(({ page }) => page === action.pageId);

            if (idx >= 0) {
                items.splice(idx, 1);
            }
        }
    },
});

const acceptBlock = updateResource({
    collection: getBlocksState,
    match: 'blockId',
    onUpdate: ({ state, action }) => {
        const page = getPageState(state, action);

        if (!page.enabled) {
            page.enabled = true;
        }
    },
});
const acceptBlocks = (state, action) => {
    getPageState(state, action).blocks = action.result;
};
const deleteBlocks = (state, action, ids) => {
    const page = getPageState(state, action);
    const allDeleted = ids.map((id) => getBlockIdsRecursively(page, page.blocks.find(isMatchId(id)))).flat();

    page.blocks = page.blocks.filter(({ _id }) => !allDeleted.includes(_id));

    if (!page.blocks.length) {
        const website = getWebsiteState(state, action);

        if (website.homePageId === page._id) {
            website.enabled = false;
        } else {
            page.enabled = false;
        }
    }
};

const acceptDomain = (state, action) => {
    const website = getWebsiteState(state, action);
    const domain = action.result || { name: '' };

    website.domain = domain;
    website.finalUrl = domain.name ? `http://${domain.name}` : website.absoluteUrl;
};

const clearCollectionInFilters = (state, action) => {
    const collectionId = action.collectionId; // changed line so git giff is more clean

    for (const { pages } of state) { //
        for (const page of pages) {
            for (const { kind, collections } of page.blocks) {
                if (kind !== ProductsBlockKind) {
                    continue;
                }

                if (collections?.include?.includes(collectionId)) {
                    collections.include = collections.include.filter((id) => id !== collectionId);
                }
            }
        }
    }
};

export default createReducer([], {
    // Websites
    [api.websites.list.success]: (state, action) => action.result,
    [api.websites.create.success]: acceptWebsite,
    [api.websites.scaffold.success]: acceptWebsite,
    [api.websites.update.success]: acceptWebsite,
    [api.websites.updateHomePage.success]: acceptWebsite,
    [api.websites.retrieve.success]: acceptWebsite,
    [api.websites.crm.settings.update.success]: acceptWebsite,
    [api.websites.delete.success]: acceptWebsite,
    [api.websites.domain.update.success]: acceptDomain,
    [api.websites.domain.delete.success]: acceptDomain,

    // Pages
    [api.pages.applyTemplate.success]: acceptWebsite,
    [api.pages.create.success]: acceptPage,
    [api.pages.update.success]: acceptPage,
    [api.pages.delete.success]: acceptPage,
    [api.pages.clone.success]: acceptPage,

    // Blocks
    [api.blocks.create.success]: acceptBlock,
    [api.blocks.update.success]: acceptBlock,
    [api.blocks.toggle.success]: acceptBlocks,
    [api.blocks.group.success]: acceptBlocks,
    [api.blocks.clone.success]: acceptBlocks,
    [api.blocks.sort.success]: acceptBlocks,
    [api.blocks.convert.success]: updateResource({ result: (result) => result.website }),
    [api.blocks.delete.success]: (state, action) => deleteBlocks(state, action, [action.blockId]),
    [api.blocks.deleteMany.success]: (state, action) => deleteBlocks(state, action, action.payload.blockId),

    [api.collections.delete.success]: clearCollectionInFilters,
});
